import { useEffect, useRef, useState } from "react";
import { Formik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { DefaultLayout } from "../../layouts/Default";
import { Main } from "../../themes/Default/components/Main";
import { Tabs } from "../../themes/Default/components/Tabs";
import { TopNavigation } from "../../themes/Default/components/TopNavigation";
import { Breadcrumps } from "../../themes/Default/components/Breadcrumps";
import { Link } from "../../themes/Default/components/Breadcrumps/components/Link";
import { Form } from "../../themes/Default/components/Form";
import { Row } from "../../themes/Default/components/Form/components/Row";
import { Field } from "../../themes/Default/components/Form/components/Row/components/Field";
import { Input } from "../../themes/Default/components/Form/components/Input";
import { useTranslation } from "react-i18next";
import { noteTypesManagement } from "../../hooks/api/NoteTypesManagement/NoteTypesManagement";
import { employeeWeeklyReportManagement } from "../../hooks/api/EmployeeWeeklyReportManagement";
import { Button, ButtonBack } from "../../themes/Default/components/TopNavigation/components/Button";
import SummerNote from "../../packages/SummerNote/SummerNote";
import { toast } from "react-toastify";
import { Title } from "../../themes/Default/components/Title";
import { roles } from "../../services/utils/Roles";
import { Textarea } from "../../themes/Default/components/Form/components/Textarea";
import { useUserToken } from "../../hooks/core/UserToken";
import { Toaster } from "../../themes/Default/components/Toaster";

const ViewWeeklyReport = () => {
    const { t } = useTranslation("common");
    const navigate = useNavigate();
    const formRef = useRef(null);
    const { employeeWeeklyReport } = useParams();
    const { useViewEmployeeWeeklyReportMutation, useEmployeeWeeklyReportNoteListMutation, useVerifyEmployeeWeeklyReportMutation } = employeeWeeklyReportManagement;
    const [viewReport] = useViewEmployeeWeeklyReportMutation();
    const [verifyReport] = useVerifyEmployeeWeeklyReportMutation();
    const { useNoteTypesListMutation } = noteTypesManagement;
    const [noteTypesList] = useNoteTypesListMutation();
    const [endDate, setEndDate] = useState([]);
    const [notesList] = useEmployeeWeeklyReportNoteListMutation();
    const [totalNotes, setTotalNotes] = useState(0);
    const [report, setReport] = useState(null);
    const [{ getDecoded }] = useUserToken(); 
    const decodedToken = getDecoded(); 
    const [disableButton, setDisableButton] = useState(true);

    const tabs = [{ title: t("details"), link: "/settings/reports", isActive: true }];

    const gotoGroupPlanningNote = () => {
        navigate("/reports/view/" + employeeWeeklyReport + "/notes");
    }

    const onVerify = () => {
        toast.warn(
            ({ closeToast }) => (
                <Toaster
                    closeToast={closeToast}
                    description={t("areYouSure")}
                    optionNo={t("no")}
                    optionYes={t("yes")}
                    onClick={() => {
                        closeToast();
                        verifyReport({employeeWeeklyReport}).then((result) => {
                            if (result?.error) {
                                toast.error(result.error.data.error.responseMessage);
                            } else {
                                toast.info(result?.data?.info?.responseMessage);
                                setDisableButton(true);
                            }
                        }); 
                    }}
                />
            ),
            {
                autoClose: false,
                progress: false,
                closeButton: false,
            },
        );     
    };

    useEffect(() => {
        viewReport(employeeWeeklyReport).then((result) => {
            formRef.current.setValues(result?.data);
            setEndDate(result?.data?.endDate);
            setReport(result?.data);
            setDisableButton(result?.isVerified);
        });

        notesList({weeklyReportId: employeeWeeklyReport}).then((result) => {
            setTotalNotes(result?.data?.totalRecords);
        });
    }, [noteTypesList]);

    return (
        <DefaultLayout>
            
            <TopNavigation>
                <ButtonBack to={'/reports'} />

                {(decodedToken?.roles.includes(roles.DIRECTOR_OF_STUDIES) || decodedToken?.roles.includes(roles.DIRECTOR) || decodedToken?.roles.includes(roles.SUPER_ADMIN) || decodedToken?.roles.includes(roles.PROFESSOR_COORDINATOR)) && (
                    <Button title={t("verify")} isDisabled={disableButton} onClick={() => onVerify()} icon="fa-regular fa-check"/>
                )}

                <Button  badgeNumber={totalNotes} title={t("Dodaj/Vidi komentare")} icon="fa-regular fa-comment-dots" onClick={() => {gotoGroupPlanningNote()}}/> 
            </TopNavigation>
            <Tabs tabs={tabs} />
            <Main>
                <Title>{report?.employeeName + " (" + report?.startDateFormated + " - " + report?.endDate + ")"}</Title>
                <Breadcrumps>
                    <Link title={t("report.title")} icon="fa-light fa-clipboard" />
                    <Link title={t("report.view")} isActive={true} />
                </Breadcrumps>
                <Formik
                    innerRef={formRef}
                    initialValues={{
                        startDate: "",
                        endDate: "",
                        progress: "",
                        teachingDifficulties: "",
                        additionalNote: ""
                    }}
                >
                    {({ values, handleChange, setFieldValue }) => (
                        <Form>
                            <Row>
                                <Field>
                                    <Input
                                        name="startDate"
                                        label={t("report.startDate")}
                                        value={values.startDateFormated}
                                        disabled={true}
                                    />
                                </Field>
                                <Field>
                                    <Input
                                        label={t("report.endDate")}
                                        value={endDate} 
                                        disabled={true}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field isFullWidth={true}>
                                    <SummerNote
                                        label={t("report.progress")}
                                        height="400px"
                                        name="progress"
                                        value={values.progress}
                                        onChange={handleChange}
                                        hint={t("report.hintForProgress")}
                                        disabled={true}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field isFullWidth={true}>
                                    <SummerNote
                                        label={t("report.teachingDifficulties")}
                                        height="400px"
                                        name="teachingDifficulties"
                                        value={values.teachingDifficulties}
                                        onChange={handleChange}
                                        hint={t("report.hintForTeachingDifficulties")}
                                        disabled={true}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field isFullWidth={true}>
                                    <SummerNote
                                        label={t("report.additionalNote")}
                                        height="400px"
                                        name="additionalNote"
                                        value={values.additionalNote}
                                        onChange={handleChange}
                                        hint={t("report.hintForAdditionalNote")}
                                        disabled={true}
                                    />
                                </Field>
                            </Row>
                            {(decodedToken?.roles.includes(roles.DIRECTOR_OF_STUDIES) || decodedToken?.roles.includes(roles.DIRECTOR) || decodedToken?.roles.includes(roles.SUPER_ADMIN) || decodedToken?.id === report?.userId) && (
                                <>
                                <Row>
                                        <Field isFullWidth={true}>
                                            <SummerNote
                                                label={t("Odrađeni nenastavni zadaci")}
                                                height="400px"
                                                name="completedTasks"
                                                value={values.completedTasks}
                                                onChange={handleChange}
                                                disabled={true}
                                            />
                                        </Field>
                                    </Row>
                                    <Row>
                                        <Field isFullWidth={true}>
                                            <SummerNote
                                                label={t("Rešeni izazovi-zapažanja")}
                                                height="400px"
                                                name="resolvedChallenges"
                                                value={values.resolvedChallenges}
                                                onChange={handleChange}
                                                disabled={true}
                                            />
                                        </Field>
                                    </Row>
                                    <Row>
                                        <Field isFullWidth={true}>
                                            <SummerNote
                                                label={t("Plan za narednu nedelju po danima")}
                                                height="400px"
                                                name="weeklyPlanByDay"
                                                value={values.weeklyPlanByDay}
                                                onChange={handleChange}
                                                disabled={true}
                                            />
                                        </Field>
                                    </Row>
                                    <Row>
                                        <Field isFullWidth={true}>
                                            <Textarea
                                                name="leaveRequestNote"
                                                label={t("Zatraži odmor")}
                                                onChange={handleChange}
                                                value={values.leaveRequestNote}
                                                rows={5}
                                                disabled={true}
                                            />
                                        </Field>
                                    </Row>
                                </>
                            )}
                        </Form>
                    )}
                </Formik>
            </Main>
        </DefaultLayout>
    );
};

export default ViewWeeklyReport;
